import React from 'react';
import { Trash2 } from "lucide-react";
import { useDispatch } from 'react-redux';
import { removeFromCart, updateCartItemQuantity } from "../../../redux/slices/cartSlice";
import { escapeHTML } from '../../utils/sanitize';
import { useOfferQuote } from '../../offers/useOffer';

const CartContents = ({ cart, userId, guestId }) => {
  const dispatch = useDispatch();
  // offer maths (BOGO etc.) comes from the offers system; this component only displays it
  const quote = useOfferQuote(cart?.products);

  const handleAddToCart = (productId, delta, quantity, size, color) => {
    const currentQuantity = Number(quantity) || 0;
    const newQuantity = currentQuantity + delta;
    if (newQuantity >= 1) {
      dispatch(updateCartItemQuantity({
        productId,
        quantity: newQuantity,
        guestId,
        userId,
        size,
        color,
      }));
    }
  };

  const handleRemoveFromCart = (productId, size, color ) => {
    dispatch(removeFromCart({ productId, guestId, userId, size, color  }));
  };

  const totalPrice = (cart?.products && Array.isArray(cart.products) && cart.products.length > 0)
    ? cart.products.reduce((total, product) => {
        return total + (Number(product?.price) || 0) * (Number(product?.quantity) || 0);
      }, 0)
    : 0;

  const totalItems = (cart?.products && Array.isArray(cart.products) && cart.products.length > 0)
    ? cart.products.reduce((total, product) => total + (Number(product?.quantity) || 0), 0)
    : 0;

  const totalSavings = (cart?.products && Array.isArray(cart.products) && cart.products.length > 0)
    ? cart.products.reduce((total, product) => {
        const original = Number(product?.originalPrice) || 0;
        const price = Number(product?.price) || 0;
        const quantity = Number(product?.quantity) || 0;
        if (original > price) return total + (original - price) * quantity;
        return total;
      }, 0)
    : 0;

  // Tags shown on the product image (max 2). Priority: offer FREE > Free Delivery (first item) > sale/stock/new
  const getProductTags = (product, index, freeQty) => {
    const tags = [];
    const quantity = Number(product?.quantity) || 1;
    if (freeQty > 0) {
      tags.push({ label: quantity > 1 ? `${freeQty} Free` : 'Free', type: 'free' });
    }
    if (index === 0) {
      tags.push({ label: 'Free Delivery', type: 'ship' });
    } else {
      const original = Number(product?.originalPrice) || 0;
      const price = Number(product?.price) || 0;
      const stock = Number(product?.stock) || 0;
      if (original > price) {
        tags.push({ label: `Saving ₹${((original - price) * quantity).toLocaleString()}`, type: 'saving' });
      } else if (stock > 0 && stock < 10) {
        tags.push({ label: `Only ${stock} left`, type: 'low' });
      } else if (product?.isNew) {
        tags.push({ label: 'New Drop', type: 'new' });
      }
    }
    return tags.slice(0, 2);
  };

  return (
    <>
      <style>{ccStyles}</style>

      {/* ── PRODUCT LIST ── */}
      <div className="cc-list">
        {(cart?.products && Array.isArray(cart.products) && cart.products.length > 0) ? (
          cart.products.map((product, index) => {
            const price = Number(product?.price) || 0;
            const quantity = Number(product?.quantity) || 0;
            const freeQty = quote.freeQtyFor(product);
            const totalItemPrice = price * quantity;
            const paidItemPrice = price * (quantity - freeQty);
            const tags = getProductTags(product, index, freeQty);

            return (
              <div key={index} className="cc-item">

                {/* Image */}
                <div className="cc-img-wrap">
                  <img
                    src={product?.image || ''}
                    alt={product?.name || 'Product'}
                    className="cc-img"
                  />
                  {tags.length > 0 && (
                    <div className="cc-tags">
                      {tags.map((t) => (
                        <span key={t.type} className={`cc-tag cc-tag--${t.type}`}>{t.label}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="cc-info">
                  <div className="cc-info-top">
                    <h3
                      className="cc-name"
                      dangerouslySetInnerHTML={{ __html: escapeHTML(product?.name || 'Unknown Product') }}
                    />
                    <button
                      className="cc-remove"
                      onClick={() => handleRemoveFromCart(product?.productId, product?.size, product?.color)}
                      aria-label="Remove item"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>

                  <p className="cc-meta">
                    {product?.size && <span>{product.size}</span>}
                    {product?.size && product?.color && <span className="cc-meta-sep">·</span>}
                    {product?.color && <span>{product.color}</span>}
                  </p>

                  <div className="cc-bottom">
                    {/* Qty controls */}
                    <div className="cc-qty">
                      <button
                        className="cc-qty-btn"
                        onClick={() => handleAddToCart(product?.productId, -1, quantity, product?.size, product?.color)}
                      >
                        —
                      </button>
                      <span className="cc-qty-num">{quantity}</span>
                      <button
                        className="cc-qty-btn"
                        onClick={() => handleAddToCart(product?.productId, 1, quantity, product?.size, product?.color)}
                      >
                        +
                      </button>
                    </div>

                    {/* Price */}
                    <div className="cc-price-wrap">
                      {freeQty > 0 && (
                        <span className="cc-price-was">₹{totalItemPrice.toLocaleString()}</span>
                      )}
                      <span className="cc-price">
                        {freeQty > 0 && paidItemPrice === 0 ? 'FREE' : `₹${paidItemPrice.toLocaleString()}`}
                      </span>
                      {quantity > 1 && (
                        <span className="cc-unit-price">₹{price.toLocaleString()} each</span>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            );
          })
        ) : (
          <div className="cc-empty">Cart is empty</div>
        )}
      </div>

      {/* ── TOTAL ── */}
      {(cart?.products && Array.isArray(cart.products) && cart.products.length > 0) && (
        <div className="cc-total-wrap">
          <div className="cc-rule"><hr /></div>

          {totalSavings > 0 && (
            <div className="cc-savings-row">
              <span className="cc-savings-label">Total Savings</span>
              <span className="cc-savings-value">— ₹{totalSavings.toLocaleString()}</span>
            </div>
          )}

          {quote.applied && (
            <>
              <div className="cc-savings-row">
                <span className="cc-savings-label">Subtotal</span>
                <span className="cc-savings-value">₹{totalPrice.toLocaleString()}</span>
              </div>
              <div className="cc-savings-row">
                <span className="cc-savings-label">{quote.offer.name}</span>
                <span className="cc-savings-value">— ₹{quote.discount.toLocaleString()}</span>
              </div>
            </>
          )}

          {quote.unitsToNextFree > 0 && (
            <p className="cc-nudge">Add 1 more eligible item — the cheaper one is free</p>
          )}
          {quote.capped && (
            <p className="cc-nudge">Maximum free items for one order applied</p>
          )}

          <div className="cc-total-row">
            <span className="cc-total-label">
              {quote.applied ? 'Total' : 'Subtotal'}
              <span className="cc-total-items"> · {totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
            </span>
            <span className="cc-total-price">₹{(quote.applied ? quote.total : totalPrice).toLocaleString()}</span>
          </div>
        </div>
      )}
    </>
  );
};

const ccStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .cc-list {
    padding-top: 20px;
    display: flex;
    flex-direction: column;
    font-family: 'Exo 2', sans-serif;
  }

  /* ── ITEM ── */
  .cc-item {
    display: flex;
    gap: 14px;
    padding: 18px 0;
    border-bottom: 1px solid rgba(0,0,0,0.07);
  }
  .cc-item:last-child { border-bottom: none; }

  /* ── IMAGE ── */
  .cc-img-wrap {
    position: relative;
    flex-shrink: 0;
    width: 80px;
    height: 96px;
  }

  .cc-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* Context tag */
  .cc-tags {
    position: absolute;
    bottom: 0;
    left: 0; right: 0;
    display: flex;
    flex-direction: column;
  }
  .cc-tag {
    font-family: 'Exo 2', sans-serif;
    font-size: 6.5px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    text-align: center;
    padding: 4px;
    line-height: 1;
  }
  .cc-tag--saving {
    background: #000;
    color: #fff;
  }
  .cc-tag--free {
    background: #000;
    color: #fff;
  }
  .cc-tag--ship {
    background: rgba(255,255,255,0.88);
    color: black;
    outline: 1px solid rgba(0,0,0,0.12);
  }
  .cc-tag--low {
    background: rgba(255,255,255,0.88);
    color: black;
    outline: 1px solid rgba(0,0,0,0.12);
  }
  .cc-tag--new {
    background: rgba(255,255,255,0.88);
    color: black;
    outline: 1px solid rgba(0,0,0,0.1);
  }

  /* ── INFO ── */
  .cc-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 0;
  }

  .cc-info-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .cc-name {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #000;
    line-height: 1.5;
    margin: 0;
    word-break: break-word;
  }

  .cc-remove {
    background: transparent;
    border: none;
    color: black;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    transition: color 0.2s ease;
  }
  .cc-remove:hover { color: #000; }

  .cc-meta {
    font-size: 9px;
    font-weight: 300;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: black;
    margin: 4px 0 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .cc-meta-sep { color: rgba(0,0,0,0.15); }

  /* ── BOTTOM ROW ── */
  .cc-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
  }

  /* Qty controls */
  .cc-qty {
    display: flex;
    align-items: center;
    border: 1px solid black;
  }

  .cc-qty-btn {
    background: transparent;
    border: none;
    color: black;
    font-family: 'Exo 2', sans-serif;
    font-size: 12px;
    font-weight: 300;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
  }
  .cc-qty-btn:hover {
    background: #000;
    color: #fff;
  }

  .cc-qty-num {
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0.1em;
    color: #000;
    width: 28px;
    text-align: center;
    border-left: 1px solid black;
    border-right: 1px solid black;
  }

  /* Price */
  .cc-price-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  .cc-price {
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.08em;
    color: #000;
  }

  .cc-price-was {
    font-size: 10px;
    font-weight: 300;
    letter-spacing: 0.08em;
    color: black;
    text-decoration: line-through;
    opacity: 0.55;
  }

  .cc-nudge {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: black;
    margin: 0 0 12px;
    line-height: 1.8;
  }

  .cc-unit-price {
    font-size: 8px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
  }

  /* ── TOTAL ── */
  .cc-total-wrap {
    padding: 16px 0 24px;
    font-family: 'Exo 2', sans-serif;
  }

  .cc-rule { padding: 0; margin-bottom: 16px; }
  .cc-rule hr {
    border: none;
    border-top: 1px solid black;
    margin: 0;
  }

  .cc-savings-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
  .cc-savings-label {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: black;
  }
  .cc-savings-value {
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.1em;
    color: black;
  }

  .cc-total-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .cc-total-label {
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: black;
  }
  .cc-total-items {
    font-weight: 300;
    color: black;
  }

  .cc-total-price {
    font-size: 18px;
    font-weight: 300;
    letter-spacing: 0.05em;
    color: #000;
  }

  .cc-empty {
    padding: 40px 0;
    text-align: center;
    font-size: 10px;
    font-weight: 300;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: black;
    font-family: 'Exo 2', sans-serif;
  }
`;

export default CartContents;
